import { isFulfilled } from '@reduxjs/toolkit'
import { FormikHelpers } from 'formik'
import React, { FC, useState } from 'react'

import Form from '../../Form'
import CustomPopup from '../../UI/Popup'
import CustomButton from '../../UI/Buttons/CustomButton'
import Label from '../../UI/Icons/Label'
import ColorPicker from '../../UI/Inputs/ColorPicker/ColorPicker'
import TextAreaInput from '../../UI/Inputs/TextAreaInput'

import { useDispatch, useSelector } from '../../../store'
import {
  createLabelThunk,
  deleteLabelThunk,
  updateLabelThunk
} from '../../../store/calendar/thunks'
import { convertHexToRGBA, formatDateToCellRefs, makeColorDarkIfItWhite } from '../helpers'

import './ModalLabel.scss'
import { useToasts } from 'react-toast-notifications'
import { TOAST_MESSAGE } from '../../UI/ToastNotification/ToastNotification'
import ConfirmPopup from '../../UI/ConfirmPopup'
import TrashIcon from '../../UI/Icons/Trash'
import DateRangePicker from '../../UI/Inputs/DateRangePicker'

type AddLabelProps = {
  project: any
  date_start: Date
  date_end: Date
  calendarDataRefs?: any
  label?: any
  calendarApi?: any
}

const ModalLabel: FC<AddLabelProps> = ({ project, date_start, date_end, label, calendarDataRefs, calendarApi }) => {
  const { isDeletingLabel, isCreatingLabel } = useSelector((state) => ({
    isCreatingLabel: state.calendarApproval.isCreatingLabel,
    isDeletingLabel: state.calendarApproval.isDeletingLabel
  }))
  const [isAddingLabel, setIsAddingLabel] = useState(false)
  const [isOpenConfirmationPopup, setIsOpenConfirmationPopup] = useState(false)
  const [startEndDatesForDatePicker, setStartEndDatesForDatePicker] = useState<[Date, Date]>([new Date(date_start), new Date(date_end)])
  const { addToast } = useToasts()
  const dispatch = useDispatch()

  const isEdit = !!label

  const setUpForm = {
    initialValues: {
      text: isEdit ? label.extendedProps.text : '',
      color: isEdit ? label.textColor : ''
    },
    validation: 'label',
    onSubmit: async (values: any, actions: FormikHelpers<any>) => {
      let dates = startEndDatesForDatePicker
      if (dates.some(date => !date)) {
        const validDate = dates.find(date => !!date)!
        dates = [validDate, validDate]
      }
      const data = {
        ...values,
        start_at: formatDateToCellRefs(dates[0]),
        end_at: formatDateToCellRefs(dates[1]),
        project_id: project.id
      }

      if (isEdit) {
        data.day_label_id = label.id

        const responseUpdateLabel = await dispatch(updateLabelThunk(data))
        if (isFulfilled(responseUpdateLabel)) {
          setIsAddingLabel(false)
          addToast(TOAST_MESSAGE.LABEL_ADDED, { appearance: 'success' })
        }
      } else {
        const responseAddLabel = await dispatch(createLabelThunk(data))
        if (isFulfilled(responseAddLabel)) {
          setIsAddingLabel(false)
          addToast(TOAST_MESSAGE.LABEL_ADDED, { appearance: 'success' })
        }
      }
    }
  }

  const onChangeDates = (dates: [Date, Date]) => {
    setStartEndDatesForDatePicker([...dates])
  }

  const closeConfirmationPopup = () => setIsOpenConfirmationPopup(false)
  const cancelLabelRemoving = () => {
    setIsAddingLabel(true)
    setIsOpenConfirmationPopup(false)
  }
  const openConfirmationPopup = () => {
    setIsAddingLabel(false)
    setIsOpenConfirmationPopup(true)
  }

  const removeLabel = async () => {
    const responseDeleteLabel = await dispatch(deleteLabelThunk(label.id))
    if (isFulfilled(responseDeleteLabel)) {
      addToast(TOAST_MESSAGE.LABEL_DELETED, { appearance: 'success' })
      setIsAddingLabel(false)
    } else {
      addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
    }
    closeConfirmationPopup()
  }

  return (
    <>
      <CustomPopup
        open={isAddingLabel}
        closeOnDocumentClick={false}
        className='add-boockmark modal-window'
        buttonClose={() => setIsAddingLabel(false)}
      >
        {
          <>
            <div className='add-boockmark__title'>{isEdit ? 'Edit ' : 'Add '} Label</div>
            <Form {...setUpForm}>
              <div className='add-boockmark__text'>
                <TextAreaInput name='text' />
              </div>
              <div className='add-boockmark__date'>
                <DateRangePicker
                  dates={startEndDatesForDatePicker}
                  datePickerProps={{ onChange: onChangeDates }}
                />
              </div>
              <div className='add-boockmark__choose-color'>
                Highlight color
                <div className='add-boockmark__choose-color__color-picker'>
                  <ColorPicker name='color' />
                </div>
              </div>
              <div className={`add-boockmark__actions ${isEdit && 'edit'}`}>
                {isEdit && (
                  <div className='add-boockmark__actions__delete'>
                    <CustomButton type='button' onClick={openConfirmationPopup} button='delete'>
                      <TrashIcon />
                    </CustomButton>
                  </div>
                )}
                <div className='add-boockmark__actions__regular'>
                  <CustomButton
                    button='secondary'
                    type='button'
                    onClick={() => {
                      setIsAddingLabel(false)
                    }}
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton button='primary' inProgress={isCreatingLabel} type='submit'>
                    Save
                  </CustomButton>
                </div>
              </div>
            </Form>
          </>
        }
      </CustomPopup>
      {!isEdit ? (
        <div onClick={() => setIsAddingLabel(true)} className='fc-daygrid-day-number__bookmark'>
          <Label stroke='#9C9EB2' />
        </div>
      ) : (
        <div
          ref={(el) => {
            if (el) {
              calendarDataRefs.current.setLabelToCell(formatDateToCellRefs(date_start), el)
            }
          }}
          onClick={() => setIsAddingLabel(true)}
          className='calendar-label'
          style={{
            backgroundColor: convertHexToRGBA(label.textColor, 0.1),
            color: makeColorDarkIfItWhite(label.textColor)
          }}
        >
          <div className='calendar-label__dot' style={{ borderColor: makeColorDarkIfItWhite(label.textColor) }}></div>
          {label.extendedProps.text}
        </div>
      )}
      <ConfirmPopup
        isModalOpen={isOpenConfirmationPopup}
        onClose={cancelLabelRemoving}
        onConfirm={removeLabel}
        isInProgress={isDeletingLabel}
      >
        Are you sure you want to delete the label?
      </ConfirmPopup>
    </>
  )
}

export default ModalLabel
